import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import ToastNotification from './components/common/ToastNotification';
import './App.css';

import Home from './pages/matrimony/Home';

// Lazy-loaded secondary route components for bundle optimization
const Login = lazy(() => import('./pages/matrimony/Auth/Login'));
const Signup = lazy(() => import('./pages/matrimony/Auth/Signup'));
const OAuthSuccess = lazy(() => import('./pages/matrimony/Auth/OAuthSuccess'));
const UserDashboard = lazy(() => import('./pages/matrimony/Dashboard/UserDashboard'));
const UserProfileEdit = lazy(() => import('./pages/matrimony/profile/UserProfileEdit'));
const UserProfile = lazy(() => import('./pages/matrimony/profile/UserProfile'));
const ProfileDetails = lazy(() => import('./pages/matrimony/profile/ProfileDetails'));
const UserMyMatches = lazy(() => import('./pages/matrimony/Matches/UserMyMatches'));
const Search = lazy(() => import('./pages/matrimony/Search/Search'));
const UserSettings = lazy(() => import('./pages/matrimony/Settings/UserSettings'));
const CompleteProfile = lazy(() => import('./pages/matrimony/profile/CompleteProfile'));
const VerificationPending = lazy(() => import('./pages/matrimony/profile/VerificationPending'));

const ForgotPassword = lazy(() => import('./pages/matrimony/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/matrimony/Auth/ResetPassword'));
const MembershipPlans = lazy(() => import('./pages/matrimony/Plans/MembershipPlans'));
const UserPlanDetails = lazy(() => import('./pages/matrimony/Plans/UserPlanDetails'));

const PrivacyPolicy = lazy(() => import('./pages/matrimony/Legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/matrimony/Legal/TermsOfService'));
const CommunityGuidelines = lazy(() => import('./pages/matrimony/Legal/CommunityGuidelines'));
const NotFound = lazy(() => import('./pages/matrimony/NotFound/NotFound'));

// Lazy-loaded Community Page Component
const CommunityHome = lazy(() => import('./pages/community/CommunityHome'));

// Page Loader Suspense Fallback
const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center vh-100 bg-white">
    <div className="spinner-border text-danger" style={{ width: '2.5rem', height: '2.5rem' }} role="status"></div>
  </div>
);

// Protected Route Wrapper Component
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <PageLoader />;
  }
  return isAuthenticated ? children : <Navigate to="/matrimony/login" replace />;
};

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <ToastNotification />
        <Router>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Primary Community & Matrimony Root Routes */}
              <Route path="/" element={<CommunityHome />} />
              <Route path="/community" element={<CommunityHome />} />
              <Route path="/matrimony" element={<Home />} />

              {/* Matrimony Public Auth & Search Routes (Canonical & Fallback) */}
              <Route path="/matrimony/login" element={<Login />} />
              <Route path="/login" element={<Navigate to="/matrimony/login" replace />} />

              <Route path="/matrimony/signup" element={<Signup />} />
              <Route path="/signup" element={<Navigate to="/matrimony/signup" replace />} />

              <Route path="/matrimony/forgot-password" element={<ForgotPassword />} />
              <Route path="/forgot-password" element={<Navigate to="/matrimony/forgot-password" replace />} />

              <Route path="/matrimony/reset-password" element={<ResetPassword />} />
              <Route path="/reset-password" element={<Navigate to="/matrimony/reset-password" replace />} />

              <Route path="/matrimony/oauth-success" element={<OAuthSuccess />} />
              <Route path="/oauth-success" element={<Navigate to="/matrimony/oauth-success" replace />} />

              <Route path="/matrimony/search" element={<Search />} />
              <Route path="/search" element={<Navigate to="/matrimony/search" replace />} />

              {/* Candidate Profile View (Canonical & Fallback) */}
              <Route path="/matrimony/profile/:identifier" element={<ProfileDetails />} />
              <Route path="/profile/:identifier" element={<ProfileDetails />} />

              {/* Legal & Compliance Public Pages */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/terms-of-use" element={<TermsOfService />} />
              <Route path="/guidelines" element={<CommunityGuidelines />} />
              <Route path="/community-guidelines" element={<CommunityGuidelines />} />

              {/* Matrimony Protected Routes (Canonical & Fallback) */}
              <Route
                path="/matrimony/dashboard"
                element={
                  <PrivateRoute>
                    <UserDashboard />
                  </PrivateRoute>
                }
              />
              <Route path="/dashboard" element={<Navigate to="/matrimony/dashboard" replace />} />

              <Route
                path="/matrimony/profile/edit"
                element={
                  <PrivateRoute>
                    <UserProfileEdit />
                  </PrivateRoute>
                }
              />
              <Route path="/profile/edit" element={<Navigate to="/matrimony/profile/edit" replace />} />

              <Route
                path="/matrimony/profile/me"
                element={
                  <PrivateRoute>
                    <UserProfile />
                  </PrivateRoute>
                }
              />
              <Route path="/profile/me" element={<Navigate to="/matrimony/profile/me" replace />} />

              <Route
                path="/matrimony/matches"
                element={
                  <PrivateRoute>
                    <UserMyMatches />
                  </PrivateRoute>
                }
              />
              <Route path="/matches" element={<Navigate to="/matrimony/matches" replace />} />

              <Route
                path="/matrimony/interests"
                element={
                  <PrivateRoute>
                    <UserMyMatches />
                  </PrivateRoute>
                }
              />
              <Route path="/interests" element={<Navigate to="/matrimony/interests" replace />} />

              <Route
                path="/matrimony/settings"
                element={
                  <PrivateRoute>
                    <UserSettings />
                  </PrivateRoute>
                }
              />
              <Route path="/settings" element={<Navigate to="/matrimony/settings" replace />} />

              <Route
                path="/matrimony/complete-profile"
                element={
                  <PrivateRoute>
                    <CompleteProfile />
                  </PrivateRoute>
                }
              />
              <Route path="/complete-profile" element={<Navigate to="/matrimony/complete-profile" replace />} />

              <Route
                path="/matrimony/plans"
                element={
                  <PrivateRoute>
                    <UserPlanDetails />
                  </PrivateRoute>
                }
              />
              <Route path="/plans" element={<Navigate to="/matrimony/plans" replace />} />

              <Route
                path="/matrimony/plans/upgrade"
                element={
                  <PrivateRoute>
                    <MembershipPlans />
                  </PrivateRoute>
                }
              />
              <Route path="/plans/upgrade" element={<Navigate to="/matrimony/plans/upgrade" replace />} />
              <Route path="/membership" element={<Navigate to="/matrimony/plans/upgrade" replace />} />

              <Route
                path="/matrimony/verification-pending"
                element={
                  <PrivateRoute>
                    <VerificationPending />
                  </PrivateRoute>
                }
              />
              <Route path="/verification-pending" element={<Navigate to="/matrimony/verification-pending" replace />} />

              {/* Public Stories Alias */}
              <Route path="/stories" element={<Navigate to="/matrimony#stories" replace />} />
              <Route path="/success-stories" element={<Navigate to="/matrimony#stories" replace />} />

              {/* 404 & Catch-all fallback */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;

